import Constants from './Constants';
/**
 * Actions that carry rendition + info_hash context and therefore must be
 * filtered against the locally active swarm. Extracted from P2PManager so the
 * logic can be unit-tested without spinning up a WebSocket stub.
 */
export function trackerActionRequiresRendition(action) {
    if (!action)
        return false;
    return (action === Constants.AnnounceEvents.Join ||
        action === Constants.AnnounceEvents.Offer ||
        action === Constants.AnnounceEvents.Offers ||
        action === Constants.AnnounceEvents.Answer ||
        action === Constants.AnnounceEvents.Candidates);
}
/**
 * Returns true when the tracker message's `protocol_version` matches the
 * locally configured one. Parity with Android `matchesCurrentProtocolVersion`
 * — messages WITHOUT a numeric `protocol_version` are rejected (a V2 tracker
 * always tags them, a V1 tracker's response is not meant to be consumed by
 * a V2 peer anyway). Filters on the narrow contract: only numeric, matching.
 */
export function messageMatchesProtocolVersion(
// eslint-disable-next-line @typescript-eslint/no-explicit-any
data, expectedVersion) {
    const received = data === null || data === void 0 ? void 0 : data.protocol_version;
    if (typeof received !== 'number')
        return false;
    return received === expectedVersion;
}
/**
 * Returns true when the tracker message is addressed to the locally active
 * swarm. Byte-for-byte parity with Android `matchesCurrentSwarmContext`:
 *   1. If the action doesn't require rendition (e.g. runtime `config`),
 *      it always passes.
 *   2. Otherwise, the message MUST carry a `rendition` field — missing
 *      rendition is rejected as malformed.
 *   3. If the message's rendition disagrees with the locally active one,
 *      reject as stale.
 *   4. For Join responses, no `info_hash` check (the local
 *      `activeInfoHash` hasn't been locked in yet).
 *   5. For every other action, the message MUST carry `info_hash`; missing
 *      or mismatched info_hash is rejected as stale.
 */
export function messageMatchesSwarm(
// eslint-disable-next-line @typescript-eslint/no-explicit-any
data, activeRendition, activeInfoHash) {
    const action = data === null || data === void 0 ? void 0 : data.action;
    if (!trackerActionRequiresRendition(action))
        return true;
    const rendition = typeof (data === null || data === void 0 ? void 0 : data.rendition) === 'string' && data.rendition.length > 0 ? data.rendition : undefined;
    if (!rendition)
        return false;
    // N1: Android always compares against `options.rendition`, even when it's
    // null — any message carrying a rendition while we don't have one locally
    // is considered stale. `if (rendition != options.rendition) return false`.
    if (rendition !== activeRendition)
        return false;
    if (action === Constants.AnnounceEvents.Join)
        return true;
    const infoHash = typeof (data === null || data === void 0 ? void 0 : data.info_hash) === 'string' && data.info_hash.length > 0 ? data.info_hash : undefined;
    if (!infoHash)
        return false;
    // Same strict comparison for info_hash on non-Join actions.
    if (infoHash !== activeInfoHash)
        return false;
    return true;
}
